import type { FormField } from '@lucid/lego';
import type { Prospect } from '../../../../../api/services/lupp/prospect/dto.prospect';
import type { ProspectTestDrive } from '../../../../../api/services/lupp/prospect/dto.test-drive-schedule';
import type { Theme } from '../utils';
import type {
  TestDriveSlotDetail,
  TestDriveVehicle,
  TestDriveWindowCombination,
} from '../../../../../api/services/lupp/test-drive/dto.test-drive';
import { TEST_DRIVE_SLOT_AVAILABLE } from '../../../../../api/services/lupp/test-drive/dto.test-drive';
import { getStatusOptions, LOCKED_TEST_DRIVE_STATUSES } from '../../../constants';
import {
  utcToLocalTimeSlot,
  utcToZonedTimeSlot,
  utcToLocalDate,
  escapeHtml,
} from '../../../../../utils/tools';
import { getHostUnavailableMessage, type SalesConsultantOption } from './utils';

/**
 * Adapts DetailsTab/formFields.ts's createTestDriveFormFields to operate on one explicit
 * schedule (new or being edited) instead of the single "active" schedule the legacy tab assumed.
 * Drops the testDriveNow toggle/dependency wiring (every row here is always "on") and the
 * checkInActionField (check-in now lives in TestDriveDetails.tsx).
 */
export type SalesConsultantFieldParams = {
  consultants: SalesConsultantOption[];
  defaultValue?: string;
  selectedHost?: string;
  hasSlot: boolean;
};

export type ScheduleTestDriveFormFieldsParams = {
  prospect?: Prospect;
  onCancel?: () => void;
  testDriveSchedule?: ProspectTestDrive;
  organizationName?: string;
  labels?: Record<string, string>;
  locale?: string;
  partnerOrgLocation?: string;
  testDriveVehicles?: TestDriveVehicle[];
  availableSlots?: TestDriveSlotDetail[];
  /** Extended only; the pickup and return times come from these instead of `availableSlots`. */
  availableWindowCombinations?: TestDriveWindowCombination[];
  handoverMinutes?: number;
  /** Without it only Standard is offered, unless the booking being edited is already extended. */
  isExtendedEnabled?: boolean;
  /** Extended only; `isAvailable: false` blocks booking and `status` says why. */
  availability?: { isAvailable?: boolean; status?: string };
  selection?: { modelTrim?: string; date?: string };
  /** Currently chosen type; falls back to the saved booking's type, then Standard. */
  testDriveType?: string;
  returnDate?: string;
  agencyTimeZone?: string;
  theme?: Theme;
  onBackToList?: () => void;
  /** Omitted when host assignment is off, which leaves the field out entirely. */
  salesConsultant?: SalesConsultantFieldParams;
  /** Requested is never offered, and a Requested test drive saves as Scheduled. */
  hideRequestedStatus?: boolean;
  /** Cancelling or marking a no-show keeps the booked slot and host as they are. */
  isClosingBooking?: boolean;
};

export const TEST_DRIVE_TYPE_STANDARD = 'STANDARD';
export const TEST_DRIVE_TYPE_EXTENDED = 'EXTENDED';

export const isExtendedTestDrive = (testDriveType?: string) =>
  testDriveType?.toUpperCase() === TEST_DRIVE_TYPE_EXTENDED;

/** The booking's saved time as a `H:mm-H:mm` slot, matching the time dropdown values. */
export const getSavedTimeSlot = (
  testDriveSchedule: ProspectTestDrive | undefined,
  agencyTimeZone: string | undefined,
  locale: string
) => {
  // An extended drive spans pickup to return, so its range is not a single slot.
  if (isExtendedTestDrive(testDriveSchedule?.testDriveType)) return '';

  const start = testDriveSchedule?.scheduledStartDatetime;
  const end = testDriveSchedule?.scheduledEndDatetime;
  return agencyTimeZone
    ? utcToZonedTimeSlot(start, end, agencyTimeZone)
    : utcToLocalTimeSlot(start, end, locale);
};

/** The time slots the API currently offers, as `H:mm-H:mm` dropdown options. */
export const getOfferedTimeOptions = (availableSlots?: TestDriveSlotDetail[]) =>
  availableSlots
    ?.filter(
      (slot) => slot.slotAvailable && slot.slotAvailabilityStatus === TEST_DRIVE_SLOT_AVAILABLE
    )
    .map((slot) => {
      const value = utcToZonedTimeSlot(slot.startTime, slot.endTime, slot.timezone);
      return { label: value.replace('-', ' - '), value };
    })
    .filter((option) => option.value) ?? [];

/** The handover takes this long when the API doesn't say. */
export const DEFAULT_HANDOVER_MINUTES = 30;

const AVAILABILITY_MESSAGES: Record<string, string> = {
  NO_DROP_OPERATING_SLOTS:
    'No operating slots are available on the return date. Please choose another date.',
};

/** Why an extended lookup is unavailable: Contentful first, then a known message, else the status itself. */
export const getAvailabilityMessage = (status = '', labels?: Record<string, string>) => {
  const readable = status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, ' ');
  return (
    labels?.[`form.availability.${status}`] ??
    AVAILABILITY_MESSAGES[status] ??
    (readable || 'No availability for the selected dates.')
  );
};

/** `9:30` + 30 -> `10:00`. */
const addMinutes = (time: string, minutes: number) => {
  const [hours, mins] = time.split(':').map(Number);
  const total = (hours * 60 + mins + minutes) % (24 * 60);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/** `2026-11-02T09:30:00` -> `9:30`, the studio's own clock time. */
const toLocalTime = (local: string) => {
  const [hours, minutes] = local.slice(11, 16).split(':');
  return `${Number(hours)}:${minutes}`;
};

const toTimeOptions = (locals: string[], handoverMinutes: number) =>
  [...new Set(locals)]
    .sort((a, b) => a.localeCompare(b))
    .map((local) => ({
      label: `${toLocalTime(local)} - ${addMinutes(toLocalTime(local), handoverMinutes)}`,
      value: toLocalTime(local),
    }));

export const getExtendedPickupOptions = (
  combinations?: TestDriveWindowCombination[],
  handoverMinutes = DEFAULT_HANDOVER_MINUTES
) =>
  toTimeOptions(
    combinations?.map((combination) => combination.pickup_datetime_local) ?? [],
    handoverMinutes
  );

/** Every return time on offer, whichever pickup time is chosen. */
export const getExtendedReturnOptions = (
  combinations?: TestDriveWindowCombination[],
  handoverMinutes = DEFAULT_HANDOVER_MINUTES
) =>
  toTimeOptions(
    (combinations ?? []).map((combination) => combination.drop_datetime_local),
    handoverMinutes
  );

/** The dropdown values a window is selected by. */
export const getWindowTimes = (combination: TestDriveWindowCombination) => ({
  pickupTime: toLocalTime(combination.pickup_datetime_local),
  returnTime: toLocalTime(combination.drop_datetime_local),
});

/** The bookable window for the chosen pickup time, narrowed by the return time once chosen. */
export const findWindowCombination = (
  combinations?: TestDriveWindowCombination[],
  pickupTime?: string,
  returnTime?: string
) =>
  pickupTime
    ? combinations?.find(
        (combination) =>
          toLocalTime(combination.pickup_datetime_local) === pickupTime &&
          (!returnTime || toLocalTime(combination.drop_datetime_local) === returnTime)
      )
    : undefined;

type GroupChildren = Extract<FormField, { fieldType: 'group' }>['children'];

const getFormTitle = (isEditing: boolean, isExtended: boolean, labels?: Record<string, string>) => {
  if (isEditing) return labels?.['form.modifyTestDrive'] ?? 'Modify Test Drive';
  return isExtended
    ? (labels?.['form.extendedTestDrive'] ?? 'Extended Test Drive')
    : (labels?.['form.scheduleTestDrive'] ?? 'Schedule Test Drive');
};

/** Extended drives call the schedule fields "Pickup", so their labels and messages differ. */
const getScheduleCopy = (isExtended: boolean, labels?: Record<string, string>) =>
  isExtended
    ? {
        dateLabel: labels?.['form.pickupDate'] ?? 'Pickup Date',
        timeLabel: labels?.['form.pickupTime'] ?? 'Pickup Time',
        dateRequired: labels?.['form.pickupDateRequired'] ?? 'Pickup date is required',
        timeRequired: labels?.['form.pickupTimeRequired'] ?? 'Pickup time is required',
      }
    : {
        dateLabel: labels?.['form.scheduledDate'] ?? 'Scheduled Date',
        timeLabel: labels?.['form.scheduledTime'] ?? 'Scheduled Time',
        dateRequired: labels?.['form.scheduledDateRequired'] ?? 'Scheduled date is required',
        timeRequired: labels?.['form.scheduledTimeRequired'] ?? 'Scheduled time is required',
      };

type SlotStateParams = {
  isExtended: boolean;
  availableSlots?: TestDriveSlotDetail[];
  availableWindowCombinations?: TestDriveWindowCombination[];
  handoverMinutes: number;
  isClosingBooking: boolean;
  savedSlot: string;
  availability?: { isAvailable?: boolean; status?: string };
  isReadyForSlots: boolean;
};

/** The time dropdown options and whether the lookup came back with nothing to book. */
const getSlotState = ({
  isExtended,
  availableSlots,
  availableWindowCombinations,
  handoverMinutes,
  isClosingBooking,
  savedSlot,
  availability,
  isReadyForSlots,
}: SlotStateParams) => {
  const offeredTimeOptions = isExtended
    ? getExtendedPickupOptions(availableWindowCombinations, handoverMinutes)
    : getOfferedTimeOptions(availableSlots);
  const returnTimeOptions = getExtendedReturnOptions(availableWindowCombinations, handoverMinutes);
  const hasSlotsAnswer = isExtended
    ? Boolean(availableWindowCombinations)
    : Boolean(availableSlots);

  // excludeBookingId makes the API offer this booking's own slot while it's still free, so the
  // options follow the API, except when closing out a booking whose slot is no longer offered.
  const keepsSavedSlot =
    isClosingBooking &&
    savedSlot &&
    !offeredTimeOptions.some((option) => option.value === savedSlot);
  const timeOptions = keepsSavedSlot
    ? [...offeredTimeOptions, { label: savedSlot.replace('-', ' - '), value: savedSlot }]
    : offeredTimeOptions;

  const isExtendedUnavailable = isExtended && availability?.isAvailable === false;
  // Only once a vehicle and dates are chosen and the call has answered, so the message never
  // flashes while the dropdown is still legitimately empty.
  const hasNoSlots =
    !isClosingBooking &&
    isReadyForSlots &&
    (isExtendedUnavailable || (hasSlotsAnswer && !timeOptions.length));

  return { timeOptions, returnTimeOptions, hasNoSlots, isExtendedUnavailable };
};

const getStatusState = (
  labels: Record<string, string> | undefined,
  isEditing: boolean,
  savedStatus: string | undefined,
  hideRequestedStatus: boolean
) => {
  const statusOptions = getStatusOptions(labels, isEditing, savedStatus).filter(
    (option) => !hideRequestedStatus || option.value !== 'requested'
  );
  const isHiddenRequested = hideRequestedStatus && savedStatus === 'requested';
  const statusDefaultValue = !savedStatus || isHiddenRequested ? 'scheduled' : savedStatus;
  return { statusOptions, statusDefaultValue };
};

type HostFieldsParams = {
  salesConsultant?: SalesConsultantFieldParams;
  labels?: Record<string, string>;
  isClosingBooking: boolean;
  isTestDriveCompleted: boolean;
};

const getHostFields = ({
  salesConsultant,
  labels,
  isClosingBooking,
  isTestDriveCompleted,
}: HostFieldsParams): { hostFields: GroupChildren; isHostUnavailable: boolean } => {
  if (!salesConsultant) return { hostFields: [], isHostUnavailable: false };

  // The selected host stays listed even when busy so the rep sees who it is next to the error.
  const selectedHost = salesConsultant.consultants.find(
    (consultant) => consultant.value === salesConsultant.selectedHost
  );
  const isHostUnavailable = Boolean(
    !isClosingBooking && salesConsultant.hasSlot && selectedHost && !selectedHost.isAvailable
  );
  const hostOptions = salesConsultant.consultants
    .filter((consultant) => consultant.isAvailable || consultant === selectedHost)
    .map(({ label, value }) => ({ label, value }));

  const hostFields: GroupChildren = [
    {
      id: 'hostUserIdentifier',
      fieldType: 'dropdown',
      name: 'hostUserIdentifier',
      apiKey: 'hostUserIdentifier',
      label: labels?.['form.assignedSalesConsultant'] ?? 'Assigned Sales Consultant',
      placeholder: labels?.['form.assignedSalesConsultantPlaceholder'] ?? 'Select Sales Consultant',
      required: !isClosingBooking,
      width: 'half',
      defaultValue: salesConsultant.defaultValue ?? '',
      options: hostOptions,
      disabled: isTestDriveCompleted || isClosingBooking || !salesConsultant.hasSlot,
      validationMessages: {
        required:
          labels?.['form.assignedSalesConsultantRequired'] ??
          'Assigned Sales Consultant is required',
      },
    },
    {
      id: 'hostUserIdentifierUnavailable',
      fieldType: 'content',
      width: 'full',
      color: 'error-1',
      size: 'small',
      content: getHostUnavailableMessage(labels),
      visible: isHostUnavailable,
    },
  ];
  return { hostFields, isHostUnavailable };
};

type FieldContext = {
  labels?: Record<string, string>;
  locale: string;
  testDriveSchedule?: ProspectTestDrive;
  isTestDriveCompleted: boolean;
  isClosingBooking: boolean;
  isExtended: boolean;
  hasModelTrim: boolean;
  hasDate: boolean;
  hasRequiredDates: boolean;
};

const getTypeFields = ({
  labels,
  isEditing,
  savedTestDriveType,
  isExtendedEnabled,
}: {
  labels?: Record<string, string>;
  isEditing: boolean;
  savedTestDriveType: string;
  isExtendedEnabled: boolean;
}): GroupChildren => {
  const offersExtended = isExtendedEnabled || savedTestDriveType === TEST_DRIVE_TYPE_EXTENDED;
  return [
    {
      id: 'testDriveType',
      fieldType: 'radio_group',
      name: 'testDriveType',
      apiKey: 'testDriveType',
      label: labels?.['form.testDriveType'] ?? 'Test Drive Type',
      width: 'full',
      layout: 'horizontal',
      defaultValue: savedTestDriveType,
      // The type decides how the booking is stored, so it is fixed once created.
      disabled: isEditing,
      options: [
        {
          label: labels?.['form.standardTestDrive'] ?? 'Standard Test Drive',
          value: TEST_DRIVE_TYPE_STANDARD,
        },
        ...(offersExtended
          ? [
              {
                label: labels?.['form.extendedTestDrive'] ?? 'Extended Test Drive',
                value: TEST_DRIVE_TYPE_EXTENDED,
              },
            ]
          : []),
      ],
    },
  ];
};

type VehicleFieldsParams = FieldContext & {
  location: string;
  savedVehicleDescription?: string;
  modelTrimOptions: { label: string; value: string }[];
  hasNoVehicles: boolean;
};

const getVehicleFields = ({
  labels,
  testDriveSchedule,
  isTestDriveCompleted,
  isClosingBooking,
  location,
  savedVehicleDescription,
  modelTrimOptions,
  hasNoVehicles,
}: VehicleFieldsParams): GroupChildren => [
  {
    fieldType: 'content',
    width: 'full',
    id: 'location',
    label: labels?.['form.location'] ?? 'Location',
    content: location,
  },
  {
    id: 'selectedModelTrim',
    fieldType: 'dropdown',
    name: 'selectedModelTrim',
    apiKey: 'selectedModelTrim',
    label: labels?.['form.selectedModeltrim'] ?? 'Selected Model/Trim',
    placeholder: labels?.['form.aModelPlaceholder'] ?? 'Select a Model',
    required: true,
    width: 'full',
    defaultValue:
      savedVehicleDescription || testDriveSchedule?.trim || testDriveSchedule?.model || '',
    options: modelTrimOptions,
    disabled: isTestDriveCompleted || isClosingBooking,
    visible: !hasNoVehicles,
    validationMessages: {
      required: labels?.['form.selectedModelTrimRequired'] ?? 'A model/trim is required',
    },
  },
  {
    id: 'selectedModelTrimUnavailable',
    fieldType: 'content',
    label: labels?.['form.selectedModeltrim'] ?? 'Selected Model/Trim',
    width: 'full',
    color: 'error-1',
    size: 'small',
    content:
      labels?.['form.noVehiclesAvailable'] ??
      'No test drive vehicles are set up for this location.',
    visible: hasNoVehicles,
  },
];

type PickupFieldsParams = FieldContext & {
  scheduleCopy: ReturnType<typeof getScheduleCopy>;
  savedSlot: string;
  timeOptions: { label: string; value: string }[];
  hasNoSlots: boolean;
  isExtendedUnavailable: boolean;
  availabilityStatus?: string;
};

const getPickupFields = ({
  labels,
  locale,
  testDriveSchedule,
  isTestDriveCompleted,
  isClosingBooking,
  hasModelTrim,
  hasRequiredDates,
  scheduleCopy,
  savedSlot,
  timeOptions,
  hasNoSlots,
  isExtendedUnavailable,
  availabilityStatus,
}: PickupFieldsParams): GroupChildren => [
  {
    id: 'scheduledDate',
    fieldType: 'date_picker',
    name: 'scheduledDate',
    apiKey: 'scheduledDate',
    label: scheduleCopy.dateLabel,
    placeholder: labels?.['form.datePlaceholder'] ?? 'Select Date',
    required: true,
    width: 'half',
    size: 'medium',
    minDate: 'today',
    defaultValue: utcToLocalDate(testDriveSchedule?.scheduledStartDatetime, locale),
    disabled: isTestDriveCompleted || isClosingBooking || !hasModelTrim,
    validationMessages: {
      required: scheduleCopy.dateRequired,
    },
  },
  {
    id: 'scheduledTime',
    fieldType: 'dropdown',
    name: 'scheduledTime',
    apiKey: 'scheduledTime',
    label: scheduleCopy.timeLabel,
    placeholder: labels?.['form.timePlaceholder'] ?? 'Select Time',
    required: !isClosingBooking,
    width: 'half',
    defaultValue: savedSlot,
    options: timeOptions,
    disabled: isTestDriveCompleted || isClosingBooking || !hasModelTrim || !hasRequiredDates,
    visible: !hasNoSlots,
    validationMessages: {
      required: scheduleCopy.timeRequired,
    },
  },
  {
    id: 'scheduledTimeUnavailable',
    fieldType: 'content',
    label: scheduleCopy.timeLabel,
    width: 'half',
    color: 'error-1',
    size: 'small',
    content: isExtendedUnavailable
      ? getAvailabilityMessage(availabilityStatus, labels)
      : (labels?.['form.noSlotsAvailable'] ??
        'No sales consultant is available on this date. Please choose another date.'),
    visible: hasNoSlots,
  },
];

type ReturnFieldsParams = FieldContext & {
  pickupDate?: string;
  returnTimeOptions: { label: string; value: string }[];
};

const getReturnFields = ({
  labels,
  locale,
  testDriveSchedule,
  isTestDriveCompleted,
  isClosingBooking,
  isExtended,
  hasDate,
  hasRequiredDates,
  pickupDate,
  returnTimeOptions,
}: ReturnFieldsParams): GroupChildren => [
  {
    id: 'returnDate',
    fieldType: 'date_picker',
    name: 'returnDate',
    apiKey: 'returnDate',
    label: labels?.['form.returnDate'] ?? 'Return Date',
    placeholder: labels?.['form.datePlaceholder'] ?? 'Select Date',
    required: true,
    width: 'half',
    size: 'medium',
    minDate: pickupDate || 'today',
    defaultValue: isExtendedTestDrive(testDriveSchedule?.testDriveType)
      ? utcToLocalDate(testDriveSchedule?.scheduledEndDatetime, locale)
      : '',
    disabled: isTestDriveCompleted || isClosingBooking || !hasDate,
    visible: isExtended,
    validationMessages: {
      required: labels?.['form.returnDateRequired'] ?? 'Return date is required',
    },
  },
  {
    id: 'returnTime',
    fieldType: 'dropdown',
    name: 'returnTime',
    apiKey: 'returnTime',
    label: labels?.['form.returnTime'] ?? 'Return Time',
    placeholder: labels?.['form.timePlaceholder'] ?? 'Select Time',
    required: !isClosingBooking,
    width: 'half',
    defaultValue: '',
    options: returnTimeOptions,
    disabled: isTestDriveCompleted || isClosingBooking || !hasRequiredDates,
    visible: isExtended,
    validationMessages: {
      required: labels?.['form.returnTimeRequired'] ?? 'Return time is required',
    },
  },
];

const getStatusField = ({
  labels,
  hasHostField,
  statusDefaultValue,
  statusOptions,
  isDisabled,
}: {
  labels?: Record<string, string>;
  hasHostField: boolean;
  statusDefaultValue: string;
  statusOptions: { label: string; value: string }[];
  isDisabled: boolean;
}): GroupChildren[number] => ({
  id: 'status',
  fieldType: 'dropdown',
  name: 'status',
  apiKey: 'status',
  label: labels?.['form.status'] ?? 'Status',
  placeholder: labels?.['form.statusPlaceholder'] ?? 'Select Status',
  required: true,
  width: hasHostField ? 'half' : 'full',
  defaultValue: statusDefaultValue,
  options: statusOptions,
  disabled: isDisabled,
  validationMessages: {
    required: labels?.['form.statusRequired'] ?? 'Status is required',
  },
});

const getTermsField = (
  labels: Record<string, string> | undefined,
  organizationName?: string
): FormField => {
  const organization = escapeHtml(organizationName || '-');
  return {
    apiKey: 'terms',
    fieldType: 'disclaimers',
    id: 'terms',
    name: 'terms',
    required: false,
    terms:
      labels?.['form.termsDisclaimer']?.replace('{{organizationName}}', organization) ??
      `By clicking Save, I confirm that I have informed the prospect that their personal data will be processed by ${organization}, Lucid Europe B.V., 
    and other relevant Lucid entities (such as local affiliates, importers, and dealers) 
    in accordance with <a href="https://lucidmotors.com/de-de/legal#privacy-policy" target="_blank" rel="noopener noreferrer">Lucid's Privacy Policy</a> and, where applicable, the Agent's own privacy policy.`,
  };
};

type ButtonsParams = {
  labels?: Record<string, string>;
  theme: Theme;
  isEditing: boolean;
  onBackToList?: () => void;
  onCancel?: () => void;
  isCancelDisabled: boolean;
  isSaveDisabled: boolean;
};

const getButtonsField = ({
  labels,
  theme,
  isEditing,
  onBackToList,
  onCancel,
  isCancelDisabled,
  isSaveDisabled,
}: ButtonsParams): FormField => ({
  id: 'formButtons',
  fieldType: 'buttonGroup',
  align: 'spaceBetween',
  buttons: [
    ...(isEditing && onBackToList
      ? [
          {
            id: 'backToListButton',
            label: labels?.['testDrive.backToList'] ?? 'Back to list',
            size: 'medium' as const,
            type: 'secondary' as const,
            color: 'neutral' as const,
            theme,
            position: 'left' as const,
            onClick: onBackToList,
          },
        ]
      : []),
    {
      id: 'cancelButton',
      label: labels?.['form.cancel'] ?? 'Cancel',
      size: 'medium',
      type: 'secondary',
      color: 'neutral',
      theme,
      isFluid: true,
      onClick: onCancel,
      disabled: isCancelDisabled,
    },
    {
      id: 'submitButton',
      label: labels?.['form.save'] ?? 'Save',
      type: 'primary',
      size: 'medium',
      color: 'neutral',
      theme,
      isFluid: true,
      disabled: isSaveDisabled,
    },
  ],
});

export const createScheduleTestDriveFormFields = ({
  prospect,
  onCancel,
  testDriveSchedule,
  organizationName,
  labels,
  locale = 'en-us',
  partnerOrgLocation,
  testDriveVehicles,
  availableSlots,
  availableWindowCombinations,
  handoverMinutes = DEFAULT_HANDOVER_MINUTES,
  isExtendedEnabled = true,
  availability,
  selection,
  testDriveType,
  returnDate,
  agencyTimeZone,
  theme = 'dark',
  onBackToList,
  salesConsultant,
  hideRequestedStatus = false,
  isClosingBooking = false,
}: ScheduleTestDriveFormFieldsParams): FormField[] => {
  const isTestDriveCompleted = testDriveSchedule?.testDriveStatus === 'completed';
  const isStatusLocked = LOCKED_TEST_DRIVE_STATUSES.includes(
    testDriveSchedule?.testDriveStatus?.toLowerCase() ?? ''
  );
  const isEditing = Boolean(testDriveSchedule?.testDriveScheduleId);

  // Each step unlocks the next: model/trim, then date, then time slot.
  const hasModelTrim = Boolean(selection?.modelTrim);
  const hasDate = Boolean(selection?.date);
  const savedTestDriveType = isExtendedTestDrive(testDriveSchedule?.testDriveType)
    ? TEST_DRIVE_TYPE_EXTENDED
    : TEST_DRIVE_TYPE_STANDARD;
  const isExtended = isExtendedTestDrive(testDriveType ?? savedTestDriveType);
  const hasReturnDate = Boolean(returnDate);
  // An extended drive needs both dates before any times are offered.
  const hasRequiredDates = hasDate && (!isExtended || hasReturnDate);

  // The dropdown is keyed by description, so map the saved model/trim onto one.
  // Falls back to any vehicle of the same model so the rep still sees Air vs Gravity.
  const savedVehicle =
    testDriveVehicles?.find(
      (vehicle) =>
        vehicle.model === testDriveSchedule?.model && vehicle.trim === testDriveSchedule?.trim
    ) ?? testDriveVehicles?.find((vehicle) => vehicle.model === testDriveSchedule?.model);

  const modelTrimOptions =
    testDriveVehicles?.map((vehicle) => ({
      label: vehicle.description,
      value: vehicle.description,
    })) ?? [];

  const savedSlot = getSavedTimeSlot(testDriveSchedule, agencyTimeZone, locale);
  const { timeOptions, returnTimeOptions, hasNoSlots, isExtendedUnavailable } = getSlotState({
    isExtended,
    availableSlots,
    availableWindowCombinations,
    handoverMinutes,
    isClosingBooking,
    savedSlot,
    availability,
    isReadyForSlots: hasModelTrim && hasRequiredDates,
  });

  // Undefined while the call is in flight, so an empty list only counts once it has answered.
  const hasNoVehicles = Array.isArray(testDriveVehicles) && testDriveVehicles.length === 0;
  // A test drive that can't be booked would fail on save, so block it up front.
  const isTestDriveUnbookable = hasNoVehicles || hasNoSlots;

  const scheduleCopy = getScheduleCopy(isExtended, labels);

  const savedStatus = testDriveSchedule?.testDriveStatus?.toLowerCase();
  const { statusOptions, statusDefaultValue } = getStatusState(
    labels,
    isEditing,
    savedStatus,
    hideRequestedStatus
  );

  const { hostFields, isHostUnavailable } = getHostFields({
    salesConsultant,
    labels,
    isClosingBooking,
    isTestDriveCompleted,
  });

  const fieldContext: FieldContext = {
    labels,
    locale,
    testDriveSchedule,
    isTestDriveCompleted,
    isClosingBooking,
    isExtended,
    hasModelTrim,
    hasDate,
    hasRequiredDates,
  };

  return [
    {
      id: 'testDriveHeader',
      fieldType: 'group',
      width: 'full',
      title: getFormTitle(isEditing, isExtended, labels),
      children: [
        ...getTypeFields({ labels, isEditing, savedTestDriveType, isExtendedEnabled }),
        ...getVehicleFields({
          ...fieldContext,
          location:
            testDriveSchedule?.appointmentLocationName ||
            partnerOrgLocation ||
            prospect?.prospectOwnerLocation ||
            '-',
          savedVehicleDescription: savedVehicle?.description,
          modelTrimOptions,
          hasNoVehicles,
        }),
        ...getPickupFields({
          ...fieldContext,
          scheduleCopy,
          savedSlot,
          timeOptions,
          hasNoSlots,
          isExtendedUnavailable,
          availabilityStatus: availability?.status,
        }),
        ...getReturnFields({
          ...fieldContext,
          pickupDate: selection?.date,
          returnTimeOptions,
        }),
        getStatusField({
          labels,
          hasHostField: Boolean(salesConsultant),
          statusDefaultValue,
          statusOptions,
          isDisabled: isTestDriveCompleted || isStatusLocked,
        }),
        ...hostFields,
      ],
    },
    getTermsField(labels, organizationName),
    getButtonsField({
      labels,
      theme,
      isEditing,
      onBackToList,
      onCancel,
      isCancelDisabled: isTestDriveCompleted,
      isSaveDisabled:
        prospect?.statusName?.toLowerCase() === 'closed' ||
        isTestDriveCompleted ||
        isTestDriveUnbookable ||
        isHostUnavailable,
    }),
  ];
};
